import type { EligibilityDetails } from '../../types/eligibility';
import { parseMeasureNumber } from '../../utils/attributeMeasure';
import { cmsQueryParamsPrefix } from '../../utils/routing';

/**
 * The reader's details, in the URL like the sort and the filters — same prefix,
 * same post-hydration read (CMS-1903 R10.4).
 *
 * ⚠️ `cms_income` puts what the reader earns into the address bar, their
 * history and any referrer. That was raised and accepted as the cost of one
 * state mechanism for the whole panel; if it is ever reopened, this pair of
 * parameters and `useProductEligibility` are the only things that change.
 */
export const CITIZENSHIP_QUERY_PARAM = `${cmsQueryParamsPrefix}citizenship`;
export const INCOME_QUERY_PARAM = `${cmsQueryParamsPrefix}income`;

export const ELIGIBILITY_QUERY_PARAMS = [
  CITIZENSHIP_QUERY_PARAM,
  INCOME_QUERY_PARAM,
] as const;

/**
 * What a URL describes, narrowed to what this page can actually act on.
 *
 * A citizenship no product carries is dropped rather than kept: it could only
 * mark every loan short, which reads as a broken page rather than as a stale
 * link. Same reasoning as `readFilterSelection`'s unrecognised-value rule.
 *
 * A non-positive or unparseable income is dropped too, which lands on the
 * resting state — no loan shows an outcome — rather than on "you earn nothing".
 */
export const readEligibilityDetails = (
  offeredCitizenships: readonly string[],
  read: (param: string) => string | null,
): EligibilityDetails => {
  const citizenship = read(CITIZENSHIP_QUERY_PARAM)?.trim();
  // The same parser the measures use: it keeps the sign, so a negative is
  // rejected below rather than stripped into a positive.
  const income = parseMeasureNumber(read(INCOME_QUERY_PARAM));

  return {
    citizenship:
      citizenship && offeredCitizenships.includes(citizenship)
        ? citizenship
        : undefined,
    monthlyIncome: income != null && income > 0 ? income : undefined,
  };
};
